import React, { useMemo } from 'react'
import { LazyMotion, domAnimation, m, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { AVAILABLE_WIDGETS, type DashboardRates } from '@/utils/abacus'
import { TrendUp as TrendUpPh, TrendDown as TrendDownPh } from '@phosphor-icons/react'

interface MarketListProps {
  rates: DashboardRates | null
  visibleWidgets: string[]
  userGroups: any[]
  onEdit?: () => void
}

const TrendUpIcon = ({ className }: { className?: string }) => (
  <TrendUpPh className={cn('w-3.5 h-3.5', className)} weight="bold" />
)

const TrendDownIcon = ({ className }: { className?: string }) => (
  <TrendDownPh className={cn('w-3.5 h-3.5', className)} weight="bold" />
)

export function MarketList({ rates, visibleWidgets, userGroups: _userGroups, onEdit }: MarketListProps) {
  const prefersReduced = useReducedMotion()
  // 🧠 Sıralama Mantığı: Kullanıcının WidgetSelector'da belirlediği sırayı (visibleWidgets) kullan.
  const sortedWidgets = useMemo(() => {
    if (!rates) return []

    // visibleWidgets ID dizisini temel alarak AVAILABLE_WIDGETS'tan objeleri çek
    return visibleWidgets
      .map(id => AVAILABLE_WIDGETS.find(w => w.id === id))
      .filter((w): w is (typeof AVAILABLE_WIDGETS)[number] => w !== undefined)
  }, [rates, visibleWidgets])

  return (
    <section className="space-y-4">
      {/* Header - Outside the box */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <h3 className="text-[15px] font-bold uppercase tracking-[0.18em] text-espresso/80">
            Piyasa Takibi
          </h3>
        </div>

        <div className="flex items-center gap-3 bg-espresso/[0.04] text-espresso/80 px-3.5 py-1.5 rounded-full font-bold uppercase tracking-[0.1em] text-[10px] shadow-[0_2px_8px_rgba(0,0,0,0.03)] border border-espresso/10">
          <div className="flex items-center gap-2">
            <div className="relative flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#800020] relative z-10 animate-[pulse_2s_ease-in-out_infinite]" />
              <div className="absolute w-full h-full rounded-full bg-[#800020] animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] opacity-40 scale-150" />
              <div className="absolute w-full h-full rounded-full bg-[#800020] animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite] opacity-20 scale-[2.5]" />
            </div>
            <span className="relative">
              CANLI
              <span className="absolute -inset-x-2 -inset-y-1 bg-espresso/5 blur-md rounded-full animate-pulse" />
            </span>
          </div>
          <div className="w-[1px] h-3 bg-espresso/15" /> {/* Divider */}
          <button
            onClick={onEdit}
            className="ai-icon-wrapper hover:scale-110 transition-all"
            title="Dizilimi Özelleştir"
          >
            <style>{`
                            .ai-anim-gear { transform-origin: 12px 12px; transition: transform 700ms cubic-bezier(0.34, 1.56, 0.64, 1) var(--ai-delay, 0ms); }
                            .ai-icon-wrapper:hover .ai-anim-gear { transform: rotate(var(--ai-rotation, 90deg)); }
                        `}</style>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              overflow="visible"
              className="animated-iconoir"
              role="img"
              aria-label="settings"
            >
              <path
                d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ai-anim-gear"
                style={{ '--ai-rotation': '90deg' } as React.CSSProperties}
              />
              <path
                d="M19.6224 10.3954L18.5247 7.7448L20 6L18 4L16.2647 5.48295L13.5578 4.36974L12.9353 2H10.981L10.3491 4.40113L7.70441 5.51596L6 4L4 6L5.45337 7.78885L4.3725 10.4463L2 11V13L4.40111 13.6555L5.51575 16.2997L4 18L6 20L7.79116 18.5403L10.397 19.6123L11 22H13L13.6045 19.6132L16.2551 18.5155C16.6969 18.8313 18 20 18 20L20 18L18.5159 16.2494L19.6139 13.598L21.9999 12.9772L22 11L19.6224 10.3954Z"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="ai-anim-gear"
                style={{ '--ai-rotation': '90deg' } as React.CSSProperties}
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Content Box */}
      <div className="bg-white rounded-[28px] border border-espresso/5 shadow-[0_12px_36px_rgba(0,0,0,0.04)] overflow-hidden">
        {/* List */}
        <div className="px-3 py-4">
          <LazyMotion features={domAnimation}>
            <div style={{ minHeight: `${Math.max(1, sortedWidgets.length) * 44}px` }}>
              {sortedWidgets.map((widget, index) => {
            const data = rates ? rates[widget.id] : null
            const isPositive = data?.rawChange !== undefined ? data.rawChange >= 0 : true
            const hasChange = data?.rawChange !== 0 && data?.change !== '...'

            let formattedLabel: string = widget.label

            return (
              <m.div
                key={widget.id}
                initial={false}
                animate={{ opacity: 1 }}
                transition={{ delay: prefersReduced ? 0 : index * 0.05 }}
                className={cn(
                  'grid grid-cols-[22px_1fr_auto_62px] items-center gap-x-1.5 p-1.5 px-3 group'
                )}
                style={{ contain: 'layout style', minHeight: '40px' }}
              >
                {/* Column 1: Trend Icon (Leftmost) */}
                <div className="flex items-center justify-center w-5 h-5">
                  <div
                    className={cn(
                      'flex items-center justify-center transition-colors',
                      !hasChange
                        ? 'text-gray-400'
                        : isPositive
                          ? 'text-emerald-600'
                          : 'text-rose-600'
                    )}
                  >
                    <div className="flex items-center justify-center scale-90">
                      {!hasChange ? (
                        <div className="text-[10px]">—</div>
                      ) : isPositive ? (
                        <TrendUpIcon />
                      ) : (
                        <TrendDownIcon />
                      )}
                    </div>
                  </div>
                </div>

                {/* Column 2: Label */}
                <div className="flex flex-col min-w-0">
                  <span className="text-[11.5px] font-bold text-espresso/80 tracking-tight truncate">
                    {formattedLabel}
                  </span>
                </div>

                {/* Column 3: Value */}
                <div className="flex justify-end">
                  <span className="text-[13.5px] font-extrabold text-espresso tracking-tight whitespace-nowrap">
                    {data?.value || '...'}
                  </span>
                </div>

                {/* Column 4: Percentage (Rightmost) */}
                <div className="flex justify-end min-w-[50px]">
                  <span
                    className={cn(
                      'text-[11px] font-bold tracking-tight whitespace-nowrap',
                      !hasChange
                        ? 'text-gray-400'
                        : isPositive
                          ? 'text-emerald-700'
                          : 'text-rose-700'
                    )}
                  >
                    {hasChange ? `%${data?.change?.replace('%', '')}` : '—'}
                  </span>
                </div>
              </m.div>
            )
          })}
            </div>
          </LazyMotion>
        </div>

      </div>
    </section>
  )
}
